import * as THREE from 'three';
import { Portal, PORTAL_HALF_W, PORTAL_HALF_H } from './Portal.js';
import { PortalHoleManager } from './PortalHoles.js';
import { EV } from '../core/EventBus.js';

const ROT180 = new THREE.Matrix4().makeRotationY(Math.PI);

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _local = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const _plane = new THREE.Plane();
const _clip = new THREE.Vector4();
const _q = new THREE.Vector4();
const _frustum = new THREE.Frustum();
const _projScreen = new THREE.Matrix4();
const _sphere = new THREE.Sphere(new THREE.Vector3(), Math.max(PORTAL_HALF_W, PORTAL_HALF_H) * 1.35);
const _uv = new THREE.Vector2();

export const PORTAL_RANGE = 140;

/** Portal pair colours, keyed by owner. */
export const PAIR_STYLES = {
  player: { a: 0x2ff3ff, b: 0xff8a1f },
  rival:  { a: 0x9dff3c, b: 0xb44cff },
};

/**
 * Placement, traversal and recursive view rendering for any number of portal
 * pairs.
 *
 * One system owns every pair rather than one system per actor, because the
 * collision hole manager has to be shared: two systems punching holes in the
 * same wall would each rebuild that wall from its own set of holes and fill in
 * the other's opening.
 */
export class PortalSystem {
  constructor({ engine, world, bus, scene }) {
    this.engine = engine;
    this.world = world;
    this.bus = bus;
    this.scene = scene;

    this.holes = new PortalHoleManager(world);

    /** @type {Map<string, {a: Portal, b: Portal}>} */
    this.pairs = new Map();
    /** @type {Portal[]} flat list, index doubles as target index */
    this.portals = [];

    this.maxDepth = engine.tier.portalDepth;
    this.viewBudget = engine.tier.portalDepth >= 2 ? 4 : 2;
    this.targets = [];
    this.cameras = [];
    this._targetSize = new THREE.Vector2(-1, -1);

    this.bodies = new Map();
    this._transform = new THREE.Matrix4();
    this._time = 0;
    this._rendered = 0;
    this.enabled = true;
    // Decaying portals: seconds a placed portal lives, or null for the
    // classic forever. Set per level (def.portalDecay) or per daily
    // mutator; measured in GAME time so CI and a 1fps phone agree.
    this.decay = null;

    this.addPair('player');
  }

  // ------------------------------------------------------------------ pairs

  addPair(owner) {
    if (this.pairs.has(owner)) return this.pairs.get(owner);
    const style = PAIR_STYLES[owner] ?? PAIR_STYLES.player;
    const a = new Portal({ color: style.a, label: `${owner}A` });
    const b = new Portal({ color: style.b, label: `${owner}B` });
    a.link = b; b.link = a;
    a.owner = b.owner = owner;
    this.scene.add(a.group, b.group);
    const pair = { a, b };
    this.pairs.set(owner, pair);
    this.portals.push(a, b);
    this._targetSize.set(-1, -1);   // force reallocation for the new slots
    this._allocate();
    for (const body of this.bodies.values()) this._resizeBody(body);
    return pair;
  }

  removePair(owner) {
    const pair = this.pairs.get(owner);
    if (!pair) return;
    for (const p of [pair.a, pair.b]) {
      this.holes.clear(p.id);
      p.group.removeFromParent();
      p.dispose();
      const i = this.portals.indexOf(p);
      if (i >= 0) this.portals.splice(i, 1);
    }
    this.pairs.delete(owner);
    this._targetSize.set(-1, -1);
    this._allocate();
    for (const body of this.bodies.values()) this._resizeBody(body);
  }

  pair(owner) { return this.pairs.get(owner); }

  portal(owner, which) {
    const p = this.pairs.get(owner);
    return p ? (which === 'a' ? p.a : p.b) : null;
  }

  isLinked(owner) {
    const p = this.pairs.get(owner);
    return !!p && p.a.active && p.b.active;
  }

  // ------------------------------------------------------------------ setup

  _allocate() {
    const w = Math.max(2, Math.floor(this.engine.width * this.engine.pixelRatio * this.engine.tier.portalScale));
    const h = Math.max(2, Math.floor(this.engine.height * this.engine.pixelRatio * this.engine.tier.portalScale));
    if (this._targetSize.x === w && this._targetSize.y === h
        && this.targets.length === this.portals.length) return;
    this._targetSize.set(w, h);

    for (const list of this.targets) for (const t of list) t.dispose();
    this.targets.length = 0;
    this.cameras.length = 0;

    for (let p = 0; p < this.portals.length; p++) {
      const rts = [];
      const cams = [];
      for (let d = 0; d < this.maxDepth; d++) {
        const rt = new THREE.WebGLRenderTarget(w, h, {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          // Half float keeps the emissive neon above 1.0 so bloom still blooms
          // when you are looking at the world through a portal.
          type: THREE.HalfFloatType,
          depthBuffer: true,
          stencilBuffer: false,
          samples: 0,
        });
        rt.texture.colorSpace = THREE.LinearSRGBColorSpace;
        rt.texture.generateMipmaps = false;
        rts.push(rt);

        const cam = new THREE.PerspectiveCamera();
        cam.matrixAutoUpdate = false;
        cam.matrixWorldAutoUpdate = false;
        cams.push(cam);
      }
      this.targets.push(rts);
      this.cameras.push(cams);
    }
  }

  resize() { this._allocate(); }

  onTier(tier) {
    this.maxDepth = tier.portalDepth;
    this.viewBudget = tier.portalDepth >= 2 ? 4 : 2;
    for (const list of this.targets) for (const t of list) t.dispose();
    this.targets.length = 0;
    this._targetSize.set(-1, -1);
    this._allocate();
  }

  // -------------------------------------------------------------- placement

  /**
   * @param {string} owner
   * @param {'a'|'b'} which
   * @returns {{ok:true, portal:Portal} | {ok:false, reason:string}}
   */
  tryPlace(owner, which, origin, dir, cameraForward) {
    const pair = this.pairs.get(owner);
    if (!pair) return { ok: false, reason: 'NO GUN' };
    const portal = which === 'a' ? pair.a : pair.b;
    const other = portal.link;

    const hit = this.world.raycast(
      origin, dir, PORTAL_RANGE,
      (c) => !c.hasTag('portal-filler'),
      { includeDisabled: true },
    );

    if (!hit) return { ok: false, reason: 'NO SURFACE IN RANGE', point: null };
    if (!hit.collider.portalable) {
      return { ok: false, reason: 'SURFACE WILL NOT HOLD', point: hit.point.clone(), normal: hit.normal.clone() };
    }

    const c = hit.collider;
    const axis = hit.axis;
    const sign = hit.sign;
    const he = [c.halfExtents.x, c.halfExtents.y, c.halfExtents.z];
    const uIdx = (axis + 1) % 3;
    const vIdx = (axis + 2) % 3;

    // Snap the portal's own up axis onto one of the face's tangent axes. This
    // keeps the opening axis aligned with the face, which makes the fit test
    // exact and the collision hole a clean rectangle.
    const wu = c.axis(uIdx, _v1);
    const wv = c.axis(vIdx, _v2);
    let upIdx;
    if (Math.abs(hit.normal.y) < 0.7) {
      upIdx = Math.abs(wu.y) >= Math.abs(wv.y) ? uIdx : vIdx;
    } else {
      const f = cameraForward || _v3.set(0, 0, -1);
      upIdx = Math.abs(wu.dot(f)) >= Math.abs(wv.dot(f)) ? uIdx : vIdx;
    }
    const rightIdx = upIdx === uIdx ? vIdx : uIdx;

    const roomUp = he[upIdx] - PORTAL_HALF_H;
    const roomRight = he[rightIdx] - PORTAL_HALF_W;
    if (roomUp < -1e-3 || roomRight < -1e-3) {
      return { ok: false, reason: 'SURFACE TOO SMALL', point: hit.point.clone(), normal: hit.normal.clone() };
    }

    _local.copy(hit.point).applyMatrix4(c.inverse);
    const cu = THREE.MathUtils.clamp(_local.getComponent(upIdx), -roomUp, roomUp);
    const cr = THREE.MathUtils.clamp(_local.getComponent(rightIdx), -roomRight, roomRight);

    const localPos = _v4.set(0, 0, 0);
    localPos.setComponent(axis, sign * he[axis]);
    localPos.setComponent(upIdx, cu);
    localPos.setComponent(rightIdx, cr);

    const hole = {
      axis,
      sign,
      u: localPos.getComponent(uIdx),
      v: localPos.getComponent(vIdx),
      halfU: uIdx === upIdx ? PORTAL_HALF_H : PORTAL_HALF_W,
      halfV: vIdx === upIdx ? PORTAL_HALF_H : PORTAL_HALF_W,
    };

    const worldPos = localPos.clone().applyMatrix4(c.matrix);

    // Overlap is checked against every live portal, not just this pair's other
    // end: two actors racing the same wall must not punch into each other.
    for (const q of this.portals) {
      if (q === portal || !q.active) continue;
      if (this._overlaps(q, worldPos, hit.normal, c, hole)) {
        const mine = q === other;
        return { ok: false, reason: mine ? 'NO ROOM BESIDE LINK' : 'SURFACE TAKEN', point: worldPos, normal: hit.normal.clone() };
      }
    }

    const upWorld = c.axis(upIdx, new THREE.Vector3());
    if (Math.abs(hit.normal.y) < 0.7) {
      if (upWorld.y < 0) upWorld.negate();
    } else if (cameraForward && upWorld.dot(cameraForward) < 0) {
      upWorld.negate();
    }

    portal.placeAt(worldPos, hit.normal, upWorld, c, { axis, sign, hole });
    // Stamped on the first update after placement, not from the last clock
    // this system happened to see: a fixed-dt stepper (the editor's walker,
    // the suite) leaves that clock ahead of the engine's, and a portal
    // stamped from it would outlive its rot by the whole gap.
    portal.placedAt = null;
    this.holes.set(c, portal.id, hole);
    this._seedDistances(portal);
    this.bus.emit(EV.PORTAL_PLACED, { owner, which, portal, position: worldPos.clone(), normal: hit.normal.clone() });
    return { ok: true, portal, point: worldPos, normal: hit.normal.clone() };
  }

  _overlaps(other, worldPos, normal, collider, hole) {
    if (other.host === collider && other.face?.axis === hole.axis && other.face?.sign === hole.sign) {
      // Same face: the rectangles are exact, so answer from them and stop.
      // Falling through to the coarser test below would reject placements two
      // portal-widths apart on a sixty metre wall.
      const o = other.face.hole;
      const gap = 0.06;
      const overlapU = Math.abs(o.u - hole.u) < (o.halfU + hole.halfU + gap);
      const overlapV = Math.abs(o.v - hole.v) < (o.halfV + hole.halfV + gap);
      return overlapU && overlapV;
    }
    // Different collider but effectively the same plane (two boxes meeting).
    if (Math.abs(other.normal.dot(normal)) > 0.985) {
      const d = Math.abs(_v1.subVectors(worldPos, other.group.position).dot(other.normal));
      if (d < 0.3) {
        other.localUV(worldPos, _uv);
        if (Math.abs(_uv.x) < PORTAL_HALF_W * 2 && Math.abs(_uv.y) < PORTAL_HALF_H * 2) return true;
      }
    }
    return false;
  }

  clear(owner, which = null) {
    const pair = this.pairs.get(owner);
    if (!pair) return;
    const list = which ? [which === 'a' ? pair.a : pair.b] : [pair.a, pair.b];
    for (const p of list) {
      if (!p.active) continue;
      this.holes.clear(p.id);
      p.deactivate();
    }
    this.bus.emit(EV.PORTAL_CLEARED, { owner, which: which || 'both' });
  }

  reset() {
    this.holes.clearAll();
    for (const p of this.portals) { p.deactivate(); p.openAmount = 0; p.group.visible = false; }
    for (const body of this.bodies.values()) { body.prev.fill(1); body.cooldown = 0; }
  }

  // -------------------------------------------------------------- traversal

  registerBody(id, body) {
    const entry = {
      id,
      getSamplePoints: body.getSamplePoints,
      onTeleport: body.onTeleport,
      prev: new Float32Array(this.portals.length).fill(1),
      cooldown: 0,
      points: [new THREE.Vector3(), new THREE.Vector3()],
    };
    this.bodies.set(id, entry);
    return entry;
  }

  unregisterBody(id) { this.bodies.delete(id); }

  _resizeBody(body) {
    const next = new Float32Array(this.portals.length).fill(1);
    next.set(body.prev.subarray(0, Math.min(next.length, body.prev.length)));
    body.prev = next;
  }

  _seedDistances(portal) {
    const idx = this.portals.indexOf(portal);
    if (idx < 0) return;
    for (const body of this.bodies.values()) {
      const pts = body.getSamplePoints(body.points);
      body.prev[idx] = this._minDistance(portal, pts);
    }
  }

  _minDistance(portal, pts) {
    let min = Infinity;
    for (const p of pts) min = Math.min(min, portal.signedDistance(p));
    return min;
  }

  /**
   * @param {string} [onlyId] restrict the crossing test to one body. The
   * movement substeps pass their own id so a fast player does not repeatedly
   * re-test a rival that has not moved; the once-per-frame call passes nothing
   * and covers everyone.
   * @returns {boolean} true if anything teleported this call.
   */
  updateTraversal(dt, onlyId = null) {
    let any = false;
    for (const body of this.bodies.values()) {
      if (onlyId !== null && body.id !== onlyId) continue;
      body.cooldown = Math.max(0, body.cooldown - dt);
      const pts = body.getSamplePoints(body.points);

      for (let i = 0; i < this.portals.length; i++) {
        const from = this.portals[i];
        const to = from.link;
        if (!from.active || !to.active) { body.prev[i] = 1; continue; }

        const d = this._minDistance(from, pts);
        const prev = body.prev[i];
        body.prev[i] = d;

        if (body.cooldown > 0) continue;
        if (!(prev > 0 && d <= 0)) continue;

        let inside = false;
        for (const p of pts) {
          from.localUV(p, _uv);
          if (Math.abs(_uv.x) < PORTAL_HALF_W + 0.25 && Math.abs(_uv.y) < PORTAL_HALF_H + 0.25) { inside = true; break; }
        }
        if (!inside) continue;

        this.getTransform(from, to, this._transform);
        body.onTeleport(this._transform, from, to);
        body.cooldown = 0.08;

        // Re-seed every portal from the new position, or the exit plane test
        // fires again the instant the body lands behind the destination.
        const after = body.getSamplePoints(body.points);
        for (let k = 0; k < this.portals.length; k++) {
          body.prev[k] = this.portals[k].active ? this._minDistance(this.portals[k], after) : 1;
        }
        this.bus.emit(EV.PLAYER_TELEPORT, { from, to, id: body.id, owner: from.owner });
        any = true;
        break;
      }
    }
    return any;
  }

  /** out = to * rotY(180) * from^-1 */
  getTransform(from, to, out) {
    out.copy(to.group.matrixWorld);
    out.multiply(ROT180);
    _inv.copy(from.group.matrixWorld).invert();
    out.multiply(_inv);
    return out;
  }

  // ---------------------------------------------------------------- render

  setDecay(seconds) {
    this.decay = Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }

  update(dt, elapsed, camera) {
    this._time = elapsed;
    if (this.decay) {
      for (const p of this.portals) {
        if (!p.active) continue;
        if (p.placedAt == null) p.placedAt = elapsed;
        const left = this.decay - (elapsed - p.placedAt);
        if (left <= 0) {
          this.holes.clear(p.id);
          p.deactivate();
          const pair = this.pairs.get(p.owner);
          this.bus.emit(EV.PORTAL_CLEARED,
            { owner: p.owner, which: pair && p === pair.a ? 'a' : 'b', decayed: true });
        } else if (left < this.decay * 0.3) {
          // The rot warning: the iris gutters and shrinks through the last
          // third of its life, so expiry is never a surprise. placeAt
          // resets the target to 1, so a re-shot portal opens clean.
          const f = left / (this.decay * 0.3);
          p._openTarget = 0.55 + 0.45 * f + Math.sin(elapsed * 24) * 0.06 * (1 - f);
        }
      }
    }
    for (const p of this.portals) {
      const dist = camera ? camera.position.distanceTo(p.group.position) : null;
      p.update(dt, elapsed, dist);
      if (camera) p.fitToCamera(camera);
    }
  }

  /** Fill every portal target. Call once per frame before the main draw. */
  renderViews(renderer, scene, camera) {
    if (!this.enabled) {
      for (const p of this.portals) p.setView(null);
      return;
    }
    const prevTarget = renderer.getRenderTarget();
    const prevXR = renderer.xr.enabled;
    renderer.xr.enabled = false;
    // A frame-wide budget, not a per-pair one: with two pairs on screen the
    // nearest openings get the real view and the far ones fall back to the
    // tunnel shader, which is indistinguishable at that size.
    this._rendered = 0;
    this._renderLevel(renderer, scene, camera, 0);
    renderer.setRenderTarget(prevTarget);
    renderer.xr.enabled = prevXR;
  }

  _renderLevel(renderer, scene, camera, depth) {
    if (depth >= this.maxDepth) {
      for (const p of this.portals) p.setView(null);
      return;
    }
    for (let i = 0; i < this.portals.length; i++) {
      const from = this.portals[i];
      const to = from.link;

      if (!to.active || this._rendered >= this.viewBudget || !this._isWorthRendering(from, camera)) {
        from.setView(null);
        continue;
      }
      this._rendered++;

      const vcam = this._virtualCamera(camera, from, to, this.cameras[i][depth]);
      this._renderLevel(renderer, scene, vcam, depth + 1);

      // The destination portal is right on top of the virtual camera; drawing
      // it would smear a one pixel sliver of its own previous frame.
      to.group.visible = false;
      renderer.setRenderTarget(this.targets[i][depth]);
      renderer.clear(true, true, false);
      renderer.render(scene, vcam);
      to.group.visible = to.active;

      from.setView(this.targets[i][depth].texture);
    }
  }

  _isWorthRendering(portal, camera) {
    if (!portal.active || portal.openAmount < 0.05) return false;
    // Behind the portal you see the wall, not the view.
    _v1.subVectors(camera.position, portal.group.position);
    if (_v1.dot(portal.normal) < -0.35) return false;

    _projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_projScreen);
    _sphere.center.copy(portal.group.position);
    return _frustum.intersectsSphere(_sphere);
  }

  _virtualCamera(camera, from, to, vcam) {
    this.getTransform(from, to, _inv);
    vcam.matrixWorld.multiplyMatrices(_inv, camera.matrixWorld);
    vcam.matrixWorld.decompose(vcam.position, vcam.quaternion, vcam.scale);
    vcam.matrixWorldInverse.copy(vcam.matrixWorld).invert();

    vcam.fov = camera.fov;
    vcam.aspect = camera.aspect;
    vcam.near = camera.near;
    vcam.far = camera.far;
    vcam.projectionMatrix.copy(camera.projectionMatrix);

    this._applyObliqueClip(vcam, to);
    vcam.projectionMatrixInverse.copy(vcam.projectionMatrix).invert();
    return vcam;
  }

  /**
   * Push the near plane onto the destination portal's surface so nothing
   * between the virtual camera and the opening is drawn. Implementation follows
   * Lengyel's oblique frustum, matching three's own Reflector.
   */
  _applyObliqueClip(vcam, to) {
    // A hair in front of the wall: the wall itself must land on the far side.
    _v1.copy(to.group.position).addScaledVector(to.normal, 0.01);
    const behind = _v2.subVectors(vcam.position, _v1).dot(to.normal);
    // Degenerate when the camera sits on the plane; the prism handles that case.
    if (behind > -0.02) return;

    _plane.setFromNormalAndCoplanarPoint(to.normal, _v1);
    _plane.applyMatrix4(vcam.matrixWorldInverse);
    _clip.set(_plane.normal.x, _plane.normal.y, _plane.normal.z, _plane.constant);

    const proj = vcam.projectionMatrix;
    const e = proj.elements;
    _q.x = (Math.sign(_clip.x) + e[8]) / e[0];
    _q.y = (Math.sign(_clip.y) + e[9]) / e[5];
    _q.z = -1.0;
    _q.w = (1.0 + e[10]) / e[14];

    const denom = _clip.dot(_q);
    if (Math.abs(denom) < 1e-6) return;
    _clip.multiplyScalar(2.0 / denom);

    e[2] = _clip.x;
    e[6] = _clip.y;
    e[10] = _clip.z + 1.0;
    e[14] = _clip.w;
  }

  dispose() {
    for (const list of this.targets) for (const t of list) t.dispose();
    for (const p of this.portals) p.dispose();
    this.holes.clearAll();
  }
}
